// Helper module for managing last opened subcourse/lesson/skill URL
// Saves the full URL with query params instead of just IDs

const STORAGE_KEY = 'lastOpenedUrl';

/**
 * Build URL with query params from IDs
 * @param {Object} data - Object with subcourse-id, lesson-id, skill-id
 * @returns {string} URL with query params
 */
export const buildUrl = (data) => {
  if (!data || !data['subcourse-id'] || !data['lesson-id'] || !data['skill-id']) {
    return null;
  }
  
  const params = new URLSearchParams({
    'subcourse-id': data['subcourse-id'],
    'lesson-id': data['lesson-id'],
    'skill-id': data['skill-id']
  });
  
  return `/learner/language-learning?${params.toString()}`;
};

/**
 * Get last opened URL from localStorage
 * @returns {string|null} Last opened URL or null if not found
 */
export const getLastOpenedUrl = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      return null;
    }
    
    // Validate it's a valid URL
    if (typeof saved !== 'string' || saved.trim() === '') {
      console.warn('lastOpened: Invalid URL format, removing');
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    return saved;
  } catch (error) {
    console.warn('lastOpened: Failed to get from localStorage:', error);
    return null;
  }
};

/**
 * Save last opened URL to localStorage
 * @param {string} url - Full URL with query params
 */
export const saveLastOpenedUrl = (url) => {
  if (!url || typeof url !== 'string') {
    console.warn('lastOpened: Cannot save invalid URL:', url);
    return;
  }
  
  try {
    localStorage.setItem(STORAGE_KEY, url);
    console.log('lastOpened: Saved URL successfully:', url);
  } catch (error) {
    console.error('lastOpened: Failed to save URL to localStorage:', error);
  }
};

/**
 * Save last opened from IDs (builds URL and saves it)
 * @param {Object} data - Object with subcourse-id, lesson-id, skill-id
 */
export const saveLastOpened = (data) => {
  const url = buildUrl(data);
  if (url) {
    saveLastOpenedUrl(url);
  } else {
    console.warn('lastOpened: Cannot build URL from data:', data);
  }
};

/**
 * Get last opened data (for backward compatibility)
 * @returns {Object|null} Last opened data or null if not found/invalid
 */
export const getLastOpened = () => {
  const url = getLastOpenedUrl();
  if (!url) {
    return null;
  }
  
  try {
    const urlObj = new URL(url, window.location.origin);
    const params = urlObj.searchParams;
    
    const subcourseId = params.get('subcourse-id');
    const lessonId = params.get('lesson-id');
    const skillId = params.get('skill-id');
    
    if (!subcourseId || !lessonId || !skillId) {
      console.warn('lastOpened: Missing required params in URL');
      return null;
    }
    
    return {
      'subcourse-id': subcourseId,
      'lesson-id': lessonId,
      'skill-id': skillId
    };
  } catch (error) {
    console.warn('lastOpened: Failed to parse URL:', error);
    return null;
  }
};

/**
 * Build practice path URL from lastOpened data
 * @param {Object} lastOpened - Last opened data
 * @returns {string|null} URL or null if data is invalid
 */
export const buildPracticePath = (lastOpened) => {
  return buildUrl(lastOpened);
};

/**
 * Validate lastOpened data structure (for backward compatibility)
 * @param {Object} data - Data to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateLastOpened = (data) => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  const hasSubcourseId = data['subcourse-id'] && typeof data['subcourse-id'] === 'string';
  const hasLessonId = data['lesson-id'] && typeof data['lesson-id'] === 'string';
  const hasSkillId = data['skill-id'] && typeof data['skill-id'] === 'string';
  
  return hasSubcourseId && hasLessonId && hasSkillId;
};

/**
 * Clear lastOpened URL from localStorage
 */
export const clearLastOpened = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('lastOpened: Cleared successfully');
  } catch (error) {
    console.warn('lastOpened: Failed to clear from localStorage:', error);
  }
};

